import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormKeys } from '@nx-customer-apps/shared/enums';
import { BuildingType } from '@nx-customer-apps/components';
import { FormOptions, QuestionOverride } from '@nx-customer-apps/shared/interfaces';
import { CommonFormService } from '../../services/common-form.service';
import { RadioQuestion, Question, TextboxQuestion } from '@nx-customer-apps/shared/utils';
import { FormToolsService, ValidatorsService } from '../../services';

@Injectable({
    providedIn: 'root'
})
export class HeatPumpIntermediaryFormService {
    private commonFormService = inject(CommonFormService);
    private validatorsService = inject(ValidatorsService);
    private formToolsService = inject(FormToolsService);

    private getDevice(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOldDeviceUsed,
            question: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.TITLE',
            styles: 'ca-radio-columns',
            value: override?.value,
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.ERRORS.REQUIRED',
            validators: [Validators.required],
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.DEVICE.DEVICE.NO', value: false }
            ]
        });
    }

    private getIncomeLowForSubsidy(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsIncomeLowForSubsidy,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.INCOME.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.INCOME.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getOwnerOfTheBuilding(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOwnerOfTheBuilding,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.BUILDING_OWNER.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.BUILDING_OWNER.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getOwnerMainResidence(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOwnerMainResidence,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.MAIN_RESIDENCE.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.MAIN_RESIDENCE.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getOldHeatingSystemDefective(override?: QuestionOverride) {
        return new RadioQuestion({
            key: FormKeys.IsOldHeatingSystemDefective,
            question: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.DEFECTIVE.TITLE',
            styles: 'ca-radio-columns',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.DEFECTIVE.ERRORS.REQUIRED',
            validators: [Validators.required],
            value: override?.value,
            options: [
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.YES', value: true },
                { key: 'HEAT_PUMP_INTERMEDIARY.HOUSEHOLD.NO', value: false }
            ]
        });
    }

    private getResidentialUnits(override?: QuestionOverride) {
        return new TextboxQuestion({
            question: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.TITLE',
            description: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.DESCRIPTION',
            defaultErrorMessage: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.REQUIRED',
            label: 'HEAT_PUMP_INTERMEDIARY.SUBSIDY.RESIDENTIAL_UNITS.LABEL',
            value: override?.value,
            key: FormKeys.NumberOfResidentialUnits,
            hints: [this.formToolsService.getNumericHint()],
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 2, max: 500 })]
        });
    }

    public getDeviceQuestions(options?: FormOptions): RadioQuestion[] {
        const deviceOverride = options?.questions?.[FormKeys.IsOldDeviceUsed];
        const device = this.getDevice(deviceOverride);

        return [device];
    }

    public getBuildingQuestions(options?: FormOptions): Question[] {
        const buildingConstructionYear = this.commonFormService.getBuildingConstructionYear(options);
        return [buildingConstructionYear];
    }

    public getSubsidyEligibilityQuestions(options?: FormOptions): (RadioQuestion | TextboxQuestion)[] {
        // Multi family house
        if (options?.buildingType === BuildingType.MultiFamilyHouse) {
            const residentialUnitsOverride = options?.questions?.[FormKeys.NumberOfResidentialUnits];
            const residentialUnits = this.getResidentialUnits(residentialUnitsOverride);
            return [residentialUnits];
        }

        // Single family house
        const incomeLowForSubsidyOverride = options?.questions?.[FormKeys.IsIncomeLowForSubsidy];
        const ownerOfTheBuildingOverride = options?.questions?.[FormKeys.IsOwnerOfTheBuilding];
        const ownerMainResidenceOverride = options?.questions?.[FormKeys.IsOwnerMainResidence];
        const incomeLowForSubsidy = this.getIncomeLowForSubsidy(incomeLowForSubsidyOverride);
        const ownerOfTheBuilding = this.getOwnerOfTheBuilding(ownerOfTheBuildingOverride);
        const ownerMainResidence = this.getOwnerMainResidence(ownerMainResidenceOverride);

        const oldHeatingSystemDefectiveOverride = options?.questions?.[FormKeys.IsOldHeatingSystemDefective];
        if (!oldHeatingSystemDefectiveOverride) {
            return [incomeLowForSubsidy, ownerOfTheBuilding, ownerMainResidence];
        }

        const oldHeatingSystemDefective = this.getOldHeatingSystemDefective(oldHeatingSystemDefectiveOverride);

        return [incomeLowForSubsidy, ownerOfTheBuilding, ownerMainResidence, oldHeatingSystemDefective];
    }
}
